"use client";

import type { ReactNode } from "react";
import { useGtmVideoTracking } from "@/hooks/use-gtm-video-tracking";
import { ImageSection } from "@/scott/ui/blocks/image-section/image-section";
import { VideoBlock } from "@/scott/ui/blocks/video/video";
import type { SectionMediaPageBlock } from "@/types/page-block";

type MediaSectionProps = SectionMediaPageBlock & {
  VideoComponent?: typeof VideoBlock;
  ImageComponent?: typeof ImageSection;
};

export function MediaSection({
  VideoComponent = VideoBlock,
  ImageComponent = ImageSection,
  ...props
}: Readonly<MediaSectionProps>) {
  const videoTracking = useGtmVideoTracking({
    src: props.media?.type === "Video" ? props.media.src : "",
    isBackgroundVideo: props.backgroundVideo,
  });

  if (!props.media) {
    return null;
  }

  let mediaContent: ReactNode;

  switch (props.media.type) {
    case "Video": {
      mediaContent = (
        <VideoComponent
          background={props.uiConfiguration?.backgroundColor}
          fullWidth={props.fullWidth}
          headline={props.headline}
          headlineColor={props.headlineColor}
          isBackgroundVideo={props.backgroundVideo}
          onEnded={videoTracking.onEnded}
          onPlay={videoTracking.onPlay}
          onReady={videoTracking.onReady}
          options={{
            autoplay: props.autoplay,
            muted: props.muted,
            loop: props.loop,
            controls: props.showControls,
          }}
          poster={props.media.poster?.src}
          progressColor={props.progressbarColor}
          src={props.media.src}
          sys={props.sys}
        />
      );
      break;
    }

    case "Image": {
      mediaContent = (
        <ImageComponent
          aspectRatio={props.aspectRatio}
          backgroundColor={props.uiConfiguration?.backgroundColor}
          columnWidth={props.columnWidth}
          fullWidth={props.fullWidth}
          image={{
            imageDesktop: {
              src: props.media.src,
              srcSet: props.media.srcSet,
            },
            imageMobile: {
              src: props.media.mobileSrc,
              srcSet: props.media.mobileSrcSet,
            },
            sizes: props.media.sizes,
            alt: props.media.alt,
          }}
          paddingBottom={props.uiConfiguration?.paddingBottom}
          paddingTop={props.uiConfiguration?.paddingTop}
          sys={props.sys}
        />
      );
      break;
    }

    default:
      mediaContent = null;
  }

  return (
    <>
      {props.anchorId && <div id={props.anchorId} />}
      {mediaContent}
    </>
  );
}
